/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { format, isValid, parse, addDays, isFuture, isSameDay } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import CheckIcon from "@material-ui/icons/Check";
import {
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
} from "@material-ui/core";

import types from "../../helpers/types";
import { formatTimeZone, utcToZonedDateTime } from "../../helpers/dateHelpers";
import BaseButton from "../common/BaseButton";
import StyledKeyboardDatePicker from "../common/StyledKeyboardDatePicker";
import StyledKeyboardTimePicker from "../common/StyledKeyboardTimePicker";
import StyledTextField from "../common/StyledTextField";
import {
  claimSpotLeg,
  removeSpotLegClaim,
} from "../../slices/updateSpotLegSlice";
import StyledSelect from "../common/StyledSelect";
import StyledFormControl from "../common/StyledFormControl";

function CarrierClaimSpotLeg({
  handleNotification,
  setProposedDepartureDateTime,
  setProposedArrivalDateTime,
  setClaimAlreadyProcessedModalOpen,
}) {
  const dispatch = useDispatch();

  const spotLegUpdate = useSelector((state) => state.updateSpotLeg);
  const spotLegDetails = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLeg
  );
  const spotLegClaim = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLegClaim
  );

  const { userPermissions } = useSelector((state) => state.userPermissions);
  const { carriers } = useSelector((state) => state.carriers);
  const isMultiLegRoute = useSelector(
    (state) => state.spotLegDetails.spotLeg.multiLegRoute
  );
  const removeClaimRequestStatus = useSelector(
    (state) => state.updateSpotLeg.removeClaimRequestStatus
  );

  // proposed departure time form state management
  const [note, setNote] = useState("");
  const [departureTime, setDepartureTime] = useState(null);
  const [departureTimeZone, setDepartureTimeZone] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [departureDateTime, setDepartureDateTime] = useState(null);
  const [proposedPickupTimeUtc, setProposedPickupTimeUtc] = useState(null);
  const [legClaimed, setLegClaimed] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [scheduledDepartureDate, setScheduledDepartureDate] = useState(
    spotLegDetails.scheduledDepartureDateTimeUTC
  );

  // departure datetime error handling
  const [invalidDepartureTime, setInvalidDepartureTime] = useState(false);
  const [invalidDepartureDate, setInvalidDepartureDate] = useState(false);
  const [departureDateErrorMessage, setDepartureDateErrorMessage] =
    useState(false);
  const [departureTimeErrorMessage, setDepartureTimeErrorMessage] =
    useState(false);

  const minDepartureDate = format(
    new Date(scheduledDepartureDate),
    "MM/dd/yyyy"
  );
  const maxDepartureDate = format(
    addDays(new Date(scheduledDepartureDate), 30),
    "MM/dd/yyyy"
  );

  useEffect(() => {
    setDefaultDepartureDateTime();

    if (spotLegClaim?.claimStatus === types.claimStatus.submitted) {
      setLegClaimed(true);
      if (isCarrierAgentUser) {
        setSelectedCarrier(spotLegClaim.carriersId);
      }

      if (spotLegClaim?.carrierNote) {
        setNote(spotLegClaim?.carrierNote);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (removeClaimRequestStatus == types.requestStatuses.success) {
      resetClaimLegForm();
    }
  }, [removeClaimRequestStatus]);

  const setDefaultDepartureDateTime = () => {
    let scheduledDepartDateTimeUTC =
      spotLegDetails.scheduledDepartureDateTimeUTC;
    let hasProposedDepartureDateTime = false;

    if (
      spotLegClaim?.claimStatus === types.claimStatus.submitted &&
      spotLegClaim.proposedPickupTimeUtc
    ) {
      scheduledDepartDateTimeUTC = spotLegClaim.proposedPickupTimeUtc;
      hasProposedDepartureDateTime = true;
    }

    const timeZone = formatTimeZone(
      scheduledDepartDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
    const dateTime = utcToZonedDateTime(
      scheduledDepartDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
    setDepartureTime(dateTime);
    setDepartureTimeZone(timeZone);
    setDepartureDate(dateTime);
    setDepartureDateTime(dateTime);
    setScheduledDepartureDate(spotLegDetails.scheduledDepartureDateTimeUTC);

    if (hasProposedDepartureDateTime) {
      setProposedDepartureDateTime(dateTime);
    }
  };

  const resetClaimLegForm = () => {
    setDefaultDepartureDateTime();
    setLegClaimed(false);
    setNote("");
    setProposedDepartureDateTime(null);
    setProposedArrivalDateTime(null);
    setProposedPickupTimeUtc(null);
    setSelectedCarrier("");
  };

  const customizedTimeTextField = (timezone) => {
    return (
          <span
            style={{ marginRight: 8, color: "rgb(73, 90, 99)" }}
            data-testid="movement-origin-facility-time-zone-name"
          >
            {timezone}
          </span>
    );
  };

  const handleNoteTextFieldChange = (event) => {
    setNote(event.target.value);
  };

  const handleLegClaimed = () => {
    setLegClaimed(true);
    setProposedDepartureDateTime(proposedPickupTimeUtc);
  };

  const handleClaimButtonClick = () => {
    let claimingCarrierId;
    if (carriers) {
      claimingCarrierId = selectedCarrier || carriers[0].id;
    }

    const proposedPickupTimeWithTz = proposedPickupTimeUtc
      ? zonedTimeToUtc(
          proposedPickupTimeUtc,
          spotLegDetails.movementOriginFacility.timeZoneName
        )
      : null;

    dispatch(
      claimSpotLeg(
        spotLegDetails.spotLegsId,
        handleNotification,
        {
          proposedPickupTimeUtc: proposedPickupTimeWithTz,
          carrierNote: note,
          carriersId: claimingCarrierId,
        },
        handleLegClaimed
      )
    );
  };

  const handleRemoveClaimButtonClick = () => {
    handleNotification();
    dispatch(
      removeSpotLegClaim(
        spotLegDetails.spotLegsId,
        setClaimAlreadyProcessedModalOpen
      )
    );
  };

  const resetFormValidation = () => {
    setInvalidDepartureDate(false);
    setDepartureDateErrorMessage(false);
    setInvalidDepartureTime(false);
    setDepartureTimeErrorMessage(false); 
  };

  const handleDepartureDateChange = (proposedDate, inputValue) => {
    resetFormValidation();      
    const isValidDate = isValid(proposedDate); 
    const dateFormat = "MM/dd/yyyy";
    const timeFormat = "HH:mm:ss";
    const scheduledDeparture = utcToZonedDateTime(
      spotLegDetails.scheduledDepartureDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
  
    if (proposedDate && isValidDate) {      
      const formattedDate = format(proposedDate, dateFormat);
      const formattedTime = format(departureDateTime, timeFormat);
      const formattedDateTime = formattedDate + formattedTime;
      const parsedDateTime = parse(
        formattedDateTime,
        dateFormat + timeFormat,
        new Date()
      );
      
      setDepartureDate(parsedDateTime);      
      setDepartureTime(parsedDateTime);
      setDepartureDateTime(parsedDateTime);

      // check if proposed datetime is equivalent to scheduled departure datetime
      parsedDateTime.setMilliseconds(0);
      parsedDateTime.setSeconds(0);
      scheduledDeparture.setMilliseconds(0);
      scheduledDeparture.setSeconds(0);

      if (parsedDateTime - scheduledDeparture === 0) {
        setProposedPickupTimeUtc(null);
      } else if (!isFuture(proposedDate) &&
      !isSameDay(proposedDate,new Date())) {
        // check if proposed datetime is before the scheduled departure datetime        
        setInvalidDepartureDate(true);
        setDepartureDateErrorMessage(true);
      } else {
        setProposedPickupTimeUtc(parsedDateTime);
      }
    } else {
      // date input invalid
      if (inputValue?.slice(-1) !== "_") {
        setInvalidDepartureDate(true);
      }
      setProposedPickupTimeUtc(null);
      setDepartureDate(null);
    }
  };

  const handleDepartureTimeChange = (proposedTime, inputValue) => {
    resetFormValidation();
    const isValidDate = isValid(proposedTime);
    const dateFormat = "MM/dd/yyyy";
    const timeFormat = "HH:mm:ss";
    const scheduledDeparture = utcToZonedDateTime(
      spotLegDetails.scheduledDepartureDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
    if (proposedTime && isValidDate) {
      const formattedDate = format(departureDateTime, dateFormat);
      const formattedTime = format(proposedTime, timeFormat);
      const formattedDateTime = formattedDate + formattedTime;
      const parsedDateTime = parse(
        formattedDateTime,
        dateFormat + timeFormat,
        new Date()
      );

      setDepartureDate(parsedDateTime);
      setDepartureTime(parsedDateTime);
      setDepartureDateTime(parsedDateTime);

      // check if proposed datetime is equivalent to scheduled departure datetime
      parsedDateTime.setSeconds(0);
      parsedDateTime.setMilliseconds(0);
      scheduledDeparture.setSeconds(0);
      scheduledDeparture.setMilliseconds(0);

      if (parsedDateTime - scheduledDeparture === 0) {
        setProposedPickupTimeUtc(null);
      } else {
        setProposedPickupTimeUtc(parsedDateTime);
      }

      // check if proposed datetime is before the scheduled departure datetime
      if (parsedDateTime < new Date()) {
        setInvalidDepartureTime(true);
        setDepartureTimeErrorMessage(true);
      }
    } else {
      // time input invalid
      if (inputValue?.slice(-1) !== "_") {
        setInvalidDepartureTime(true);
      }
      setProposedPickupTimeUtc(null);
      setDepartureTime(null);      
      setDepartureTimeErrorMessage(false);
    }
  };

  const handleSelectedCarrierChange = (event) => {
    setSelectedCarrier(event.target.value);
  };

  const claimRequestInProgress =
    spotLegUpdate.claimSpotLegRequestStatus ===
    types.requestStatuses.inProgress;

  const removeClaimRequestInProgress =
    removeClaimRequestStatus === types.requestStatuses.inProgress;

  const isCarrierAgentUser = carriers.length > 1;
  return (
    <>
      <Grid container spacing={2}>
        {!isMultiLegRoute && (
          <>
            <Grid item xs={3}>
              <StyledKeyboardDatePicker
                label="Date"
                placeholder="MM/DD/YY"
                variant="inline"
                inputVariant="outlined"
                fullWidth={true}
                value={departureDateTime}
                onChange={handleDepartureDateChange}
                disabled={legClaimed}
                maxDate={maxDepartureDate}
                minDate={minDepartureDate}
                disablePast={true}
                inputProps={{
                  "data-testid": "update-departure-date-text-field",
                  readOnly: false,
                }}
                error={invalidDepartureDate}
                helperText={
                  departureDateErrorMessage && departureDate !== null
                  ? "Date should not be before today"
                  :
                  invalidDepartureDate && departureDate === null
                  ? "Invalid date format. The date format is MM/DD/YY"
                  : undefined

                }
                format="MM/dd/yy"
                InputLabelProps={{ shrink: true }}                   
              />
            </Grid>
            <Grid item xs={3}>
              <StyledKeyboardTimePicker
                label="Time"
                inputVariant="outlined"
                fullWidth={true}
                error={invalidDepartureTime}
                helperText={
                  departureTimeErrorMessage && departureTime !== null
                  ? "Date/Time should not be before now"
                  :
                  invalidDepartureTime && departureTime === null
                  ? "Invalid time format. The time format is HH:MM"
                  : undefined
                }
                key="departure-time"
                value={departureDateTime}
                onChange={handleDepartureTimeChange}
                disabled={legClaimed}
                ampm={false}
                keyboardIcon={false}
                keyboardIcon = {customizedTimeTextField(departureTimeZone)}
                KeyboardButtonProps={{ disabled: true, size:'small' }}
                inputProps={{
                  "data-testid": "update-departure-time-text-field",
                }} 
              />
            </Grid>
          </>
        )}
        {!isMultiLegRoute && (
          <Grid item xs={6}>
            <StyledTextField
              label={proposedPickupTimeUtc ? "Note (required)" : "Note"}
              variant="outlined"
              fullWidth={true}
              multiline={true}
              value={note}
              onChange={handleNoteTextFieldChange}
              disabled={legClaimed}
              inputProps={{
                maxLength: 2048,
                "data-testid": "claim-note-text-field",
              }}
            />
          </Grid>
        )}
        {legClaimed && !isCarrierAgentUser && (
          <Grid item xs={3}>
            <Button
              style={{ margin: "8px 0px" }}
              size="large"
              onClick={handleRemoveClaimButtonClick}
              data-testid="carrier-remove-claim-btn"
            >
              Remove Claim
            </Button>
          </Grid>
        )}
        {userPermissions.claimsEnabled &&
          legClaimed &&
          !isCarrierAgentUser &&
          isMultiLegRoute && (
            <Grid item xs={4}>
              <BaseButton
                style={{ width: "100%", margin: "8px 0px" }}
                onClick={handleClaimButtonClick}
                data-testid="spot-leg-claimed-button"
                disabled={legClaimed}
              >
                Leg Claimed
                <CheckIcon fontSize="small" style={{ marginLeft: 8 }} />
              </BaseButton>
            </Grid>
          )}
        {userPermissions.claimsEnabled && (
          <>
            {isCarrierAgentUser && (
              <Grid item xs={legClaimed && !isMultiLegRoute ? 5 : 6}>
                <StyledFormControl variant="outlined" fullWidth={true}>
                  <InputLabel id="carrier-label">Select a carrier</InputLabel>
                  <StyledSelect
                    value={selectedCarrier}
                    label="Select a carrier"
                    labelId="carrier-label"
                    onChange={handleSelectedCarrierChange}
                    data-testid="assigned-carrier-select"
                    disabled={legClaimed}
                  >
                    {carriers.map((carrier) => (
                      <MenuItem key={carrier.id} value={carrier.id}>
                        {carrier.carrierName}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </StyledFormControl>
              </Grid>
            )}
            {isMultiLegRoute && (
              <Grid
                item
                container={true}
                xs={legClaimed && !isCarrierAgentUser ? 5 : 6}
              >
                <StyledTextField
                  label="Note"
                  variant="outlined"
                  fullWidth={true}
                  multiline={true}
                  value={note}
                  onChange={handleNoteTextFieldChange}
                  disabled={legClaimed}
                  inputProps={{
                    maxLength: 240,
                    "data-testid": "claim-note-text-field",
                  }}
                />
              </Grid>
            )}
            {!legClaimed && (
              <Grid
                item
                container={true}
                justify="center"
                xs={
                  (isMultiLegRoute && !isCarrierAgentUser) ||
                  (!isMultiLegRoute && isCarrierAgentUser)
                    ? 6
                    : 12
                }
              >
                <BaseButton
                  style={{
                    width:
                      (isMultiLegRoute && !isCarrierAgentUser) ||
                      (!isMultiLegRoute && isCarrierAgentUser)
                        ? "100%"
                        : "50%",
                    height: "36.5px",
                    margin: "8px 0px",
                  }}
                  onClick={handleClaimButtonClick}
                  data-testid="spot-leg-claim-button"
                  disabled={
                    (proposedPickupTimeUtc && !note) ||
                    !departureDate ||
                    !departureTime ||
                    (isCarrierAgentUser && !selectedCarrier) ||
                    claimRequestInProgress
                  }
                  startIcon={
                    claimRequestInProgress ? (
                      <CircularProgress size={15} />
                    ) : null
                  }
                >
                  Claim Leg
                </BaseButton>
              </Grid>
            )}
          </>
        )}
        {userPermissions.claimsEnabled && legClaimed && isCarrierAgentUser && (
          <>
            <Grid item xs={3} style={{ margin: "8px 0px" }}>
              <Button
                size="large"
                onClick={handleRemoveClaimButtonClick}
                data-testid="carrier-agent-remove-claim-btn"
                disabled={removeClaimRequestInProgress}
              >
                Remove Claim
              </Button>
            </Grid>

            <Grid item xs={4}>
              <BaseButton
                style={{ width: "100%", margin: "8px 0px" }}
                onClick={handleClaimButtonClick}
                data-testid="spot-leg-claimed-button"
                disabled={legClaimed}
              >
                Leg Claimed{" "}
                <CheckIcon fontSize="small" style={{ marginLeft: 8 }} />
              </BaseButton>
            </Grid>
          </>
        )}
        {userPermissions.claimsEnabled &&
          legClaimed &&
          !isCarrierAgentUser &&
          !isMultiLegRoute && (
            <Grid item xs={4}>
              <BaseButton
                style={{ width: "100%", margin: "8px 0px" }}
                onClick={handleClaimButtonClick}
                data-testid="spot-leg-claimed-button"
                disabled={legClaimed}
              >
                Leg Claimed
                <CheckIcon fontSize="small" style={{ marginLeft: 8 }} />
              </BaseButton>
            </Grid>
          )}
      </Grid>
    </>
  );
}

CarrierClaimSpotLeg.propTypes = {
  handleNotification: PropTypes.func.isRequired,
  setProposedDepartureDateTime: PropTypes.func.isRequired,
  setProposedArrivalDateTime: PropTypes.func.isRequired,
  setClaimAlreadyProcessedModalOpen: PropTypes.func.isRequired,
};

export default CarrierClaimSpotLeg;
